import { Box, IconButton, styled, useTheme } from "@mui/material";
import { memo, useState, useRef, useEffect } from "react";
import { useWidth } from "@/shared/hooks/use-width.hook";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";

interface MxCImageCarouselProps {
  images: string[];
  renderItem: (image: string, index: number) => React.ReactNode;
  onClick?: () => void;
  showIndicators?: boolean;
}

const MxCImageCarousel = memo((props: MxCImageCarouselProps) => {
  const width = useWidth();
  const theme = useTheme();
  const isMobile = ["xs", "sm"].includes(width);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentIndex < props.images.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
    if (isRightSwipe && currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < props.images.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.style.transform = `translateX(-${currentIndex * 100}%)`;
    }
  }, [currentIndex]);

  return (
    <CarouselWrapper>
      <CarouselContainer
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          style={{
            width: "100%"
          }}
        >
          {props.images.map((image, index) => props.renderItem(image, index))}
        </div>
      </CarouselContainer>

      {!isMobile && props.images.length > 1 && (
        <>
          <NavButton
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="prev"
          >
            <IconChevronLeft
              size={30}
              stroke={2}
              color={theme.palette.primary.main}
            />
          </NavButton>

          <NavButton
            onClick={handleNext}
            disabled={currentIndex === props.images.length - 1}
            className="next"
          >
            <IconChevronRight
              size={30}
              stroke={2}
              color={theme.palette.primary.main}
            />
          </NavButton>
        </>
      )}

      {props.showIndicators !== false && (
        <Indicators>
          {props.images.map((_, index) => (
            <Indicator
              key={index}
              active={currentIndex === index}
              onClick={() => setCurrentIndex(index)}
            />
          ))}
        </Indicators>
      )}
    </CarouselWrapper>
  );
});

export default MxCImageCarousel;

MxCImageCarousel.displayName = "MxCImageCarousel";

const CarouselWrapper = styled(Box)({
  width: "100%",
  position: "relative",
  overflow: "hidden"
});

const CarouselContainer = styled(Box)({
  display: "flex",
  transition: "transform 0.3s ease-in-out",
  width: "100%",
  height: "100%",
  "& > div": {
    display: "flex",
    width: "100%",
    "& > *": {
      flex: "0 0 100%",
      width: "100%"
    }
  }
});

const NavButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  background: "rgba(255, 255, 255, 0.7)",
  width: 30,
  height: 30,
  zIndex: 2,
  fontSize: 20,
  color: theme.palette.primary.main,
  transition: "background-color 0.2s",

  "&:hover": {
    background: "rgba(255, 255, 255, 0.9)"
  },

  "&.Mui-disabled": {
    opacity: 0.5,
    cursor: "not-allowed"
  },

  "&.prev": {
    left: 10
  },

  "&.next": {
    right: 10
  }
}));

const Indicators = styled(Box)({
  position: "absolute",
  bottom: 10,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  gap: 8,
  zIndex: 2
});

const Indicator = styled(IconButton)<{ active?: boolean }>(({ theme, active }) => ({
  width: 8,
  height: 8,
  padding: 0,
  minWidth: 8,
  background: active ? theme.palette.primary.main : "rgba(255, 255, 255, 0.7)",
  transition: "background-color 0.2s",
  svg: {
    width: 20,
    height: 20
  },

  "&:hover": {
    background: theme.palette.primary.main
  }
}));
